import HelpIcon from '@mui/icons-material/HelpOutline'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import DatePicker from 'components/DatePicker'
import TimePicker from 'components/TimePicker'
import { isBefore } from 'date-fns'
import { useAsyncAction } from 'hooks/useAsyncAction'
import { useMountEffect } from 'hooks/useMountEffect'
import { useTodayStart } from 'hooks/useTodayStart'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { from } from 'rxjs'
import { setSchedule } from 'services/compose'
import { composerScheduleSelector, composerSelectedProfilesCountSelector } from 'services/compose/selectors'
import { getPostScheduleFromComposerState, MIN_REPEAT_COUNT, parseRepeatCount } from 'services/compose/utils'
import { checkFeatureAvailability } from 'services/product'
import { defaultTimezoneSelector, getDefaults } from 'services/settings'
import { FEATURE_POST_REPEAT } from 'shared/constants'
import { RepeatPeriod } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import { MS_IN_MINUTE } from 'utils/constants'
import { resolveTimezone } from 'utils/timezone'
import styles from './ComposerScheduleForm.pcss'
import { REPEAT_OPTIONS } from 'shared'

const SCHEDULE_ERROR_INVALID_TIME = 'Please select a time to post that is in the future.'

export interface ComposerScheduleFormProps {
  submitDisabled: boolean
  className?: string
  hideActions?: boolean
  onSubmit: () => void
  onSubmitCreateNew?: () => void
  onCancel: () => void
  onError: (e: Error) => void
}

export function ComposerScheduleForm(props: ComposerScheduleFormProps) {
  const { onSubmit, onError, onSubmitCreateNew } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const schedule = useSelector(composerScheduleSelector)
  const selectedProfilesCount = useSelector(composerSelectedProfilesCountSelector)
  const [error, setError] = React.useState<null | string>(null)
  const [repeatCountInput, setRepeatCountInput] = React.useState<null | string>(null)
  const defaultTimezone = useSelector(defaultTimezoneSelector)
  // EXPL: settings defaults are fetched on mount and `defaults.timezone` is '' until they land.
  // `TZDate.tz('')` throws, so submitting in that window failed with an opaque RangeError.
  // `getPostScheduleFromComposerState` resolves the same way, so what is shown here, what is
  // validated below and what `createPost` schedules are always the same zone.
  const timezone = resolveTimezone(defaultTimezone)
  const [fetchDefaults] = useAsyncAction(() => from(dispatch(getDefaults()).unwrap()))

  useMountEffect(() => {
    // EXPL: `settings.defaults` is persisted, so the stored timezone survives a reload and can be
    // stale — changed in the legacy app or on another device. Refetching on every composer open is
    // what keeps scheduling honest; skipping it silently posts against the old zone forever.
    fetchDefaults()
  })

  // EXPL: the "time is in the past" error is only meaningful for the time that produced it, so
  // editing either half of the schedule clears it instead of leaving it up until the next submit.
  const onTimeChange = React.useCallback((t: Date) => {
    setError(null)
    dispatch(setSchedule({ time: t.toISOString() }))
  }, [dispatch])

  const onDateChange = React.useCallback((value: Date) => {
    setError(null)
    dispatch(setSchedule({
      date: value.toISOString(),
      repeatUntilDate: isBefore(value, schedule.repeatUntilDate) ? schedule.repeatUntilDate : value.toISOString()
    }))
  }, [dispatch, schedule.repeatUntilDate])

  const onRepeatChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked
    // EXPL: only gate turning repeat ON — gating both directions left a downgraded user, whose
    // composer still carries `repeat: true` from before the downgrade, unable to switch it off.
    if (value && !dispatch(checkFeatureAvailability(FEATURE_POST_REPEAT))) {
      return
    }
    // EXPL: `getPostScheduleFromComposerState` drops the repeat when repeatCount <= 0, so turning
    // repeat on has to carry a usable count rather than trust whatever the state happens to hold.
    dispatch(setSchedule(value
      ? { repeat: true, repeatCount: Math.max(schedule.repeatCount, MIN_REPEAT_COUNT) }
      : { repeat: false }
    ))
  }, [dispatch, schedule.repeatCount])

  // EXPL: the field holds the raw string while it is being edited, so backspacing to retype does
  // not snap 5 to 1 mid-keystroke and make the field impossible to clear. Only a valid count
  // reaches the store; blur drops the draft, so an empty field falls back to the last valid one.
  const onRepeatCountChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setRepeatCountInput(raw)
    const count = parseRepeatCount(raw)
    if (count !== null) {
      dispatch(setSchedule({ repeatCount: count }))
    }
  }, [dispatch])

  const onRepeatCountBlur = React.useCallback(() => {
    setRepeatCountInput(null)
  }, [])

  // EXPL: the draft is only the string being typed. Anything that moves the stored count — turning
  // repeat on floors it — has to win over it, otherwise the field keeps rendering a number the
  // store no longer holds until it happens to lose focus. A draft that parses to nothing leaves the
  // store alone, so clearing the field to retype it survives this.
  React.useEffect(() => {
    setRepeatCountInput(null)
  }, [schedule.repeatCount])

  const onRepeatPeriodChange = React.useCallback((e: SelectChangeEvent) => {
    dispatch(setSchedule({ repeatPeriod: e.target.value as RepeatPeriod }))
  }, [dispatch])

  const onRepeatDateChange = React.useCallback((value: Date) => {
    dispatch(setSchedule({
      repeatUntil: 'date',
      repeatUntilDate: value.toISOString()
    }))
  }, [dispatch])

  const onRepeatUntilChange = React.useCallback((e: React.ChangeEvent<any>) => {
    const value = e.target.value
    dispatch(setSchedule({ repeatUntil: value }))
  }, [dispatch])

  const submitWith = React.useCallback((callback: () => void) => {
    try {
      const postSchedule = getPostScheduleFromComposerState(schedule, timezone)
      const now = new Date()
      if (now.getTime() + MS_IN_MINUTE - new Date(postSchedule.postDate).getTime() > 0) {
        setError(SCHEDULE_ERROR_INVALID_TIME)
        return
      }
      setError(null)

      callback()
    } catch (e) {
      onError(e)
    }
  }, [onError, schedule, timezone])

  const submit = React.useCallback(() => submitWith(onSubmit), [submitWith, onSubmit])

  const submitCreateNew = React.useCallback(() => {
    if (onSubmitCreateNew) {
      submitWith(onSubmitCreateNew)
    }
  }, [submitWith, onSubmitCreateNew])

  // EXPL: these have to be memoized. Both pickers are `memo`d on a shallow prop compare, so a fresh
  // Date built inline here would re-render the whole picker subtree every time an unrelated part of
  // the composer store changed.
  const scheduleTime = React.useMemo(() => new Date(schedule.time), [schedule.time])
  const scheduleDate = React.useMemo(() => new Date(schedule.date), [schedule.date])
  const repeatUntilDate = React.useMemo(() => new Date(schedule.repeatUntilDate), [schedule.repeatUntilDate])

  // EXPL: a stable Date that still follows the clock across midnight — see the hook for why both
  // halves of that matter to a memoized picker.
  const minDate = useTodayStart()

  return (
    <section className={props.className || ''} data-testid="composer-schedule">
      <h4 className={styles.heading}>
        <FormattedMessage id="composer.labels.schedule-title" />
      </h4>
      <div className={styles.row}>
        <div>
          <TimePicker value={scheduleTime} ampm onChange={onTimeChange} />
        </div>
        <div>
          <DatePicker
            value={scheduleDate}
            onChange={onDateChange}
            minDate={minDate}
          />
        </div>
        <div className={styles['tz-box']}>
          {timezone}
          <Tooltip placement="top" title={<FormattedMessage id="composer.labels.timezone-hint" />}>
            <HelpIcon className={styles['icon-help']} />
          </Tooltip>
        </div>
      </div>

      <div className={styles.row}>
        <div>
          <span>
            <FormControlLabel
              label={<FormattedMessage id="composer.labels.repeat" />}
              classes={{ label: styles.label, root: styles['cb-root'] }}
              control={(
                <Checkbox
                  checked={schedule.repeat}
                  color="primary"
                  onChange={onRepeatChange}
                />
              )}
            />
          </span>
        </div>
      </div>
      <div className={`${styles.row} ${styles['row-until']}`}>
        <div>
          <span>
            <TextField
              type="number"
              value={repeatCountInput ?? schedule.repeatCount}
              className={styles['input-number']}
              disabled={!schedule.repeat}
              slotProps={{ htmlInput: { min: MIN_REPEAT_COUNT } }}
              onChange={onRepeatCountChange}
              onBlur={onRepeatCountBlur}
            />
          </span>
        </div>
        <div>
          <span>
            <Select
              value={schedule.repeatPeriod}
              onChange={onRepeatPeriodChange}
              disabled={!schedule.repeat}
              className={styles['repeat-period']}
              classes={{ select: styles.select }}
              MenuProps={{
                classes: { paper: styles['repeat-for-menu'] }
              }}
            >
              {
                REPEAT_OPTIONS.map(option => (
                  <MenuItem
                    key={option.value}
                    className={styles['repeat-for-menu-item']}
                    value={option.value}
                  >
                    <FormattedMessage id={`composer.labels.${option.label}`} />
                  </MenuItem>
                ))
              }
            </Select>
          </span>
        </div>
        <div className={styles['group-until']}>
          <span>
            <span className={`${styles.label}`}>
              <FormattedMessage id="composer.labels.until" />:
            </span>
          </span>
          <span className={styles['radio-group-wrapper']}>
            <RadioGroup
              value={schedule.repeatUntil}
              onChange={onRepeatUntilChange}
              className={styles['radio-group']}
            >
              <FormControlLabel
                className={styles.label}
                value="forever"
                classes={{ label: styles.label }}
                control={<Radio className={styles['radio-btn']} color="primary" disabled={!schedule.repeat} />}
                label={<FormattedMessage id="composer.labels.forever" />}
              />
              <FormControlLabel
                className={styles.label}
                value="date"
                control={<Radio className={styles['radio-btn']} color="primary" disabled={!schedule.repeat} />}
                label={(
                  <span className={styles['calendar-until']}>
                    <DatePicker
                      value={repeatUntilDate}
                      minDate={scheduleDate}
                      disabled={!schedule.repeat}
                      onChange={onRepeatDateChange}
                    />
                  </span>
                )}
              />
            </RadioGroup>
          </span>
        </div>
      </div>

      <div className={styles.actions} data-testid="composer-schedule-actions">
        {error && (
          <div className={styles.error}>{error}</div>
        )}
        {!props.hideActions && (
          <div className={styles['buttons-wrapper']}>
            <Button onClick={props.onCancel} className={styles.button}>
              <FormattedMessage id="actions.cancel" />
            </Button>
            <Button
              className={styles.button}
              variant="contained"
              color="primary"
              disabled={props.submitDisabled}
              onClick={submit}
            >
              <FormattedMessage id="composer.actions.schedule" values={{ count: selectedProfilesCount }} />
            </Button>
            {onSubmitCreateNew && (
              <Button
                className={styles.button}
                variant="contained"
                color="primary"
                disabled={props.submitDisabled}
                data-testid="composer-schedule-submit-create-new"
                onClick={submitCreateNew}
              >
                <FormattedMessage id="composer.actions.schedule-create-new" />
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default ComposerScheduleForm
